import { supabase } from './supabase';
import type { Tenant } from './database.types';
import { resolveTenant } from './tenant';

/**
 * Chargement des tenants depuis Supabase (US-402).
 * Remplace définitivement l'array `TENANTS` codé en dur du legacy (audit dette D5).
 *
 * Objectif (critère d'acceptation US-402) : ajouter un client en base = il apparaît
 * PARTOUT automatiquement (site client, switcher admin, tableau de bord parc),
 * sans aucune modification de code.
 */

/** Colonnes chargées pour identifier + brander un tenant. */
const TENANT_COLUMNS =
  'id, slug, name, currency, tax_rate, primary_color, secondary_color, brand_color, delivery_enabled, delivery_min_cents, delivery_zones, tagline, short_pitch, hero_image_url, city, address_line, postal_code, phone1, email, logo_url, opening_hours, slot_step_min, prep_time_min, slot_capacity, closures, socials, rating_google, rating_google_count, rating_uber, rating_uber_count, billing_status, trial_ends_at, billing_notes, odoo_partner_id, manager_name, manager_phone, manager_email, opened_on, crm_stage, crm_notes, loyalty_enabled, loyalty_threshold, loyalty_reward_label, stripe_charges_enabled, trust_items, tablet_pin, created_at, site_status';
// `trust_items` (SQL_P0) et `tablet_pin` (SQL_P2) : colonnes créées en base le 2026-06-16
// (SQL consolidé exécuté) → désormais chargées ici (atouts vitrine sur-mesure + code tablette).

/**
 * Charge le tenant correspondant au hostname courant.
 * Résolution par slug (issu du hostname complet, US-407).
 * Retourne null si non trouvé (le caller affiche un fallback propre).
 */
export async function loadTenantBySlug(slug: string): Promise<Tenant | null> {
  if (!slug) return null;

  // ilike sans joker = égalité insensible à la casse (routing /r/<slug> tolérant à la casse).
  const { data, error } = await supabase
    .from('tenants')
    .select(TENANT_COLUMNS)
    .ilike('slug', slug)
    .maybeSingle();

  if (error) {
    console.warn('[tenants] chargement échoué:', error.message);
    return null;
  }
  return (data as Tenant) ?? null;
}

/**
 * Charge un tenant par son DOMAINE PERSONNALISÉ (ex. « commande.chezmomo.fr »).
 * Permet à un resto d'utiliser son propre nom de domaine au lieu de yumlink.io/r/slug.
 * Le domaine perso est stocké dans `tenants.custom_domain` (posé sur validation).
 * Best-effort : si la colonne n'existe pas encore (avant migration) ou aucun match,
 * renvoie null → le caller retombe sur la résolution par slug (aucune régression).
 */
export async function loadTenantByCustomDomain(hostname: string): Promise<Tenant | null> {
  const host = (hostname || '').toLowerCase().replace(/^www\./, '').trim();
  if (!host) return null;
  const { data, error } = await supabase
    .from('tenants')
    .select(TENANT_COLUMNS)
    .ilike('custom_domain', host)
    .maybeSingle();
  if (error) {
    // Colonne absente (migration pas encore passée) ou autre → repli silencieux.
    return null;
  }
  return (data as Tenant) ?? null;
}

/**
 * Résout le tenant depuis le hostname courant :
 *  1) domaine 100 % perso du resto (match exact sur `custom_domain`), sinon
 *  2) slug déduit du hostname (sous-domaine `resto.yumlink.io`, ou fallback legacy).
 * (Le parcours par défaut `yumlink.io/r/<slug>` passe, lui, par loadTenantBySlug —
 *  cette fonction n'est appelée que pour un accès par nom d'hôte.)
 */
export async function loadTenantByHostname(hostname: string): Promise<Tenant | null> {
  const byDomain = await loadTenantByCustomDomain(hostname);
  if (byDomain) return byDomain;
  const { slug } = resolveTenant(hostname);
  return loadTenantBySlug(slug);
}

/**
 * Charge TOUS les tenants (pour la console SuperAdmin : switcher + tableau parc).
 * Une seule source de vérité, partagée par toutes les vues admin (fin du double
 * affichage hardcodé/BD constaté dans le legacy).
 */
export async function loadAllTenants(): Promise<Tenant[]> {
  const { data, error } = await supabase
    .from('tenants')
    .select(TENANT_COLUMNS)
    .order('name');

  if (error) {
    console.warn('[tenants] chargement liste échoué:', error.message);
    return [];
  }
  return (data as Tenant[]) ?? [];
}
